import {
  Box,
  HStack,
  Stack,
  Text,
  useControllableState,
  useMediaQuery,
} from "@chakra-ui/react"
import { ChevronRightIcon } from "Icons/ChevronRightIcon"
import { MenuHamburgerIcon } from "Icons/MenuHamburgerIcon"
import { HEADER_NAVBAR_HEIGHT } from "UsabilityHub/constants/layoutConstants"
import { last } from "lodash"
import React, { PropsWithChildren, useEffect } from "react"

type RootProps = {
  label?: string
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
}

export const Root = ({
  isOpen: isOpenProp,
  label = "Menu",
  onOpenChange,
  children,
}: PropsWithChildren<RootProps>) => {
  const [isOpen, setIsOpen] = useControllableState({
    value: isOpenProp,
    onChange: onOpenChange,
  })

  const [isSmall] = useMediaQuery("(max-width: 30rem)")

  // For now, collapsible menus are only supported on small screens
  useEffect(() => {
    // If we're moving from large to small screen, we want to collapse the menu.
    // If we're moving from small to large screen, we want to open the menu.
    setIsOpen(!isSmall)
  }, [isSmall])

  const register = (el: HTMLDivElement) => {
    if (!el) return

    const navigation = last(
      Array.from(
        el.ownerDocument.querySelectorAll("[data-application-navbar]")
      ) as HTMLElement[]
    )

    let navigationBottom = 0

    const resize = () => {
      navigationBottom = navigation
        ? navigation.offsetTop + navigation.offsetHeight
        : 0

      const top = el.offsetTop - navigationBottom
      el.style.setProperty(
        "--navigation-menu-scrollable-top",
        `${navigationBottom}px`
      )
      el.style.setProperty("--navigation-menu-sticky-top", `${top}px`)
    }

    resize()

    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(el)
    el.ownerDocument.documentElement.addEventListener("resize", () =>
      requestAnimationFrame(resize)
    )

    const scrolled = () => {
      const scrollTop = Math.min(
        el.ownerDocument.documentElement.scrollTop,
        navigationBottom
      )
      el.style.setProperty("--navigation-menu-scroll-offset", `${scrollTop}px`)
    }

    el.ownerDocument.addEventListener("scroll", () => {
      requestAnimationFrame(scrolled)
    })

    scrolled()

    // TODO: once we're on React 19
    // return () => {
    //   resizeObserver.disconnect()
    //   window.removeEventListener("resize", resize)
    //   window.removeEventListener("scroll", scrolled)
    // }
  }

  return (
    <Stack
      ref={register}
      aria-expanded={isOpen}
      data-navigation-menu
      w={{ base: "100vw", sm: "var(--navigation-menu-width)" }}
      maxW="100vw"
      bgColor="ds.surface.sunken"
      gap={0}
      sx={{
        "--header-navbar-height": HEADER_NAVBAR_HEIGHT,
        "--navigation-menu-footer-height": "0px",
        "--navigation-menu-width": "21rem",
        "--navigation-menu-sticky-top": "0px",
        "--navigation-menu-scrollable-top": "0px",
        "--navigation-menu-scroll-offset": "0px",
      }}
      flexShrink={0}
      borderColor="ds.border.default"
      borderRightWidth={1}
    >
      <HStack
        as="button"
        textAlign="left"
        p={2}
        pl={{ base: 4, sm: 2 }}
        gap={4}
        display={{ base: "flex", sm: "none" }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <MenuHamburgerIcon
          boxSize={4}
          m={2}
          display={{ base: "none", sm: "block" }}
        />
        <Text as="div" textStyle="ds.interface.large" flexGrow={1}>
          {label}
        </Text>
        <ChevronRightIcon
          boxSize={4}
          m={2}
          display={{ base: "block", sm: "none" }}
          transform={isOpen ? "rotate(90deg)" : "rotate(0deg)"}
          transition="transform 0.2s ease-in-out"
        />
      </HStack>
      <Box
        display={{ base: "block", sm: "contents" }}
        overflowY="hidden"
        transition="height 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
        sx={{
          interpolateSize: "allow-keywords",
          "[aria-expanded=false] &": {
            height: "0px",
          },
        }}
      >
        {children}
      </Box>
    </Stack>
  )
}
